/**
 * Le battement qui tient les radios éveillées, et son interrupteur.
 *
 * # Le problème qu'il vise
 *
 * Mesuré le 2026-09-11 entre deux machines sur le même Wi-Fi : un `ping` donne
 * un minimum de cinq millisecondes, une médiane de vingt, et des pointes à
 * cent quatre-vingt-dix. Ce n'est pas le réseau, c'est la carte Wi-Fi du
 * récepteur qui s'endort dès qu'elle n'a rien à envoyer : le point d'accès
 * garde alors ce qui lui est destiné jusqu'à la prochaine balise, et une note
 * peut attendre toute une période de veille. Un iPad fait pareil, et plus
 * volontiers encore. C'est la queue de la distribution des trajets, celle qui
 * décide du tampon — le p90 la voit passer à chaque salve.
 *
 * # Le pari
 *
 * Un trafic régulier, même minuscule, tient la plupart des cartes en mode
 * actif : leur veille dynamique ne s'enclenche qu'après un silence. D'où un
 * battement de six octets à cadence fixe, que le SFU relaie à tout le monde —
 * chaque radio de la salle voit alors passer quelque chose en permanence, dans
 * les deux sens.
 *
 * # La mesure
 *
 * Le 2026-09-13, sur l'iPad, depuis le poste qui héberge le SFU :
 *
 * ```
 * ping -c 100            rtt min/avg/max/mdev = 4.170/26.111/145.817/37.160 ms
 * ping -i 0.05 -c 400    rtt min/avg/max/mdev = 3.992/5.439/19.812/1.915 ms
 * ```
 *
 * Un paquet toutes les cinquante millisecondes, et la radio ne s'endort plus :
 * la moyenne tombe de vingt-six à cinq, le maximum de cent quarante-six à
 * vingt. Le pari est gagné au niveau du réseau ; il l'est en séance si le
 * trajet mesuré par l'iPad suit.
 *
 * Il est donc **allumé par défaut**, à cette cadence. `__virtuosa.keepalive(0)`
 * le coupe, pour mesurer sans lui ou économiser une batterie ; le réglage
 * survit au rechargement, comme le journal de notes, pour la même raison. Il
 * coûte une vingtaine de datagrammes par seconde et par joueur, quelques
 * kilo-octets par seconde en tout, et une radio qui ne dort pas le temps
 * d'une séance — c'est le prix, et il est bien moindre que celui des notes
 * qu'elle laissait attendre.
 *
 * # Qui bat
 *
 * Un joueur, et personne d'autre : la cadence réglée ici ne vaut que pour le
 * rôle `player`, et c'est le contexte de séance qui la pose, sur le billet.
 *
 * Dans un récital, le spectateur attend quatre secondes de plus que son
 * trajet (voir `LISTENER_HORIZON_MS`), et une radio endormie ne retient une
 * note que le temps d'une période de veille : l'avance l'absorbe, chez lui
 * comme chez l'interprète, dont les notes arrivent à un public qui les attend
 * de toute façon. Le battement n'y achetait rien, et c'est là qu'il coûtait le
 * plus : celui de l'interprète partait vers tout le public, vingt fois par
 * seconde et par spectateur.
 */

const STORAGE_KEY = "virtuosa.live.keepalive-ms";

/**
 * La cadence par défaut : sous la veille dynamique la plus courte des pilotes
 * courants, et celle qu'a validée la mesure ci-dessus.
 */
export const DEFAULT_KEEPALIVE_MS = 50;

let intervalMs = DEFAULT_KEEPALIVE_MS;
let restored = false;

/**
 * La cadence en vigueur, en millisecondes. Zéro : pas de battement.
 *
 * Sans réglage enregistré, la cadence par défaut ; un zéro enregistré est un
 * choix, et il tient.
 */
export function radioKeepaliveMs(): number {
  if (!restored) {
    restored = true;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw === null ? DEFAULT_KEEPALIVE_MS : Number(raw);
      intervalMs = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    } catch {
      // Mode privé, cookies refusés : la cadence par défaut, sans bruit.
    }
  }

  return intervalMs;
}

/** Règle la cadence. Zéro ou moins l'éteint. Prend effet à la prochaine séance. */
export function setRadioKeepaliveMs(ms: number): void {
  restored = true;
  intervalMs = Number.isFinite(ms) && ms > 0 ? Math.round(ms) : 0;

  try {
    localStorage.setItem(STORAGE_KEY, String(intervalMs));
  } catch {
    // Le réglage en mémoire suffit pour la session en cours.
  }
}
